import * as React from "react";
import {
  Grid,
  Button,
  Box,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";

import axiosInstance from "../../../../common/helpers/axios.instance";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";

import "./appversion.css";

// Sortable Card Component using dnd-kit
function SortableCard({ post, handleEdit, handleDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: post.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Grid 
      item 
      xs={12} 
      sm={6} 
      md={4}
      mb={2}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <Box
        sx={{
          p: 1,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          boxShadow: isDragging ? 4 : 1,
          backgroundColor: "#fff",
          position: "relative",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1,
            }}
          >
            {/* Drag Handle */}
            <IconButton
              {...listeners}
              sx={{
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
              }}
            >
              <DragIndicatorIcon />
            </IconButton>

            {/* Action Buttons */}
            <Box>
              <IconButton color="primary">
                {post.orderNumber}
              </IconButton>
              <IconButton color="primary" onClick={() => handleEdit(post)}>
                <EditIcon />
              </IconButton>
              <IconButton color="error" onClick={() => handleDelete(post.id)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              textAlign: "left",
              ml: 1,
            }}
          >
            <Box
              sx={{
                color: "primary.main",
                fontSize: "1.3rem",
                mb: 1,
                mt: 5,
              }}
            >
              {post.title}
            </Box>
            <Box 
              className="blog-content"
              sx={{ 
                color: "text.primary",
                "& p": { margin: 0 },
                "& *": { fontSize: "0.9rem !important" },
                "& a": { 
                  color: "primary.main",
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "none",
                  }
                }
              }}
              dangerouslySetInnerHTML={{ __html: post.contentTrimed }}
            />
            
            {post.youtubeVideoId && (
              <Box sx={{ mt: 2 }}>
                <iframe
                  width="100%"
                  height="100"
                  src={`https://www.youtube.com/embed/${post.youtubeVideoId}`}
                  title="YouTube video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ borderRadius: "8px" }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Grid>
  );
}

export default function Blog() {
  const [showModal, setShowModal] = React.useState(false);
  const [allPosts, setAllPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [title, setTitle] = React.useState("");
  const [content, setContent] = React.useState("");
  const [youtubeVideoId, setYoutubeVideoId] = React.useState("");

  const [editingPostId, setEditingPostId] = React.useState(null);
  const [currentEditingPost, setCurrentEditingPost] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Quill editor configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ color: [] }, { background: [] }],
      ["link"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
  ];

  const processLinks = (html) => {
    if (!html) return "";
    return html.replace(
      /<a\s+href=/gi,
      '<a target="_blank" rel="noopener noreferrer" href='
    );
  };

  // Sanitize HTML content to prevent XSS attacks
  const sanitizeHTML = (html) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3',
        'ul', 'ol', 'li', 'a', 'span'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
      ALLOW_DATA_ATTR: false,
    });
  };

  // Validate YouTube Video ID format
  const validateYouTubeId = (id) => {
    if (!id || id.trim() === "") return "";
    // YouTube IDs are 11 characters: alphanumeric, underscore, and hyphen
    const youtubeIdRegex = /^[a-zA-Z0-9_-]{11}$/;
    const trimmedId = id.trim();
    
    if (youtubeIdRegex.test(trimmedId)) {
      return trimmedId;
    }
    
    // Try to extract ID from full URL if user pasted URL
    const urlMatch = trimmedId.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (urlMatch) {
      return urlMatch[1];
    }
    
    return ""; // Invalid format
  };

  // Sanitize text to prevent XSS in plain text fields
  const sanitizeText = (text) => {
    return text.trim().replace(/<[^>]*>/g, ''); // Remove any HTML tags
  };

  const fetchAllBlogPosts = async () => {
    try {
      const res = await axiosInstance.get(
        process.env.REACT_APP_BASE_API + "/allposts"
      );

      const posts = res.data.data.posts.map((post) => {
        // Sanitize content from backend
        const sanitizedContent = sanitizeHTML(post.content || "");
        const strippedContent = sanitizedContent.replace(/<[^>]*>/g, "");
        const trimmedContent = strippedContent.length > 150
          ? sanitizedContent.substring(0, 200) + "..."
          : sanitizedContent;
        
        return {
          ...post,
          title: sanitizeText(post.title || ""),
          content: sanitizedContent,
          contentTrimed: processLinks(trimmedContent),
          youtubeVideoId: validateYouTubeId(post.youtubeVideoId || ""),
        };
      }).sort((a, b) => a.orderNumber - b.orderNumber);

      setAllPosts(posts);
    } catch (error) {
      console.log({ error });
    }
  };

  React.useEffect(() => {
    fetchAllBlogPosts();
  }, []);

 
  React.useEffect(() => {
    if (showModal && currentEditingPost) {
      setTitle(currentEditingPost.title || "");
      setContent(currentEditingPost.content || "");
      setYoutubeVideoId(currentEditingPost.youtubeVideoId || "");
    } else if (showModal && !currentEditingPost) {
      setTitle("");
      setContent("");
      setYoutubeVideoId("");
    }
  }, [showModal, currentEditingPost]);

  const handleChange = () => {
    setEditingPostId(null);
    setCurrentEditingPost(null);
    setShowModal(true);
  };

  const handleEdit = (post) => {
    setEditingPostId(post.id);
    setCurrentEditingPost(post);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this post?"
    );
    if (!confirmDelete) return;

    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_BASE_API}/posts/${id}`
      );
      setAllPosts((prev) => prev.filter((post) => post.id !== id));
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  const handleSavePost = async () => {
    setLoading(true);

    if (!title.trim()) {
      alert("Titulli është i detyrueshëm!");
      setLoading(false);
      return;
    }

    if (!content.trim()) {
      alert("Përmbajtja është e detyrueshme!");
      setLoading(false);
      return;
    }

    const sanitizedTitle = sanitizeText(title);
    const sanitizedContent = sanitizeHTML(content);
    const validatedVideoId = validateYouTubeId(youtubeVideoId);

    if (sanitizedTitle.length > 200) {
      alert("Titulli është shumë i gjatë! Maksimumi 200 karaktere.");
      setLoading(false);
      return;
    }

    if (youtubeVideoId.trim() && !validatedVideoId) {
      alert("YouTube Video ID është i pavlefshëm! Vendosni një ID të vlefshëm ose lëreni bosh.");
      setLoading(false);
      return;
    }

    const postData = {
      title: sanitizedTitle,
      content: sanitizedContent,
      youtubeVideoId: validatedVideoId,
    };

    try {
      if (editingPostId) {
    
        await axiosInstance.put(
          `${process.env.REACT_APP_BASE_API}/posts/${editingPostId}`,
          postData
        );

        setAllPosts((prev) =>
          prev.map((p) => (p.id === editingPostId ? { ...p, ...postData } : p))
        );
      } else {
         
        const res = await axiosInstance.post(
          `${process.env.REACT_APP_BASE_API}/posts`,
          postData
        );
      }

      setShowModal(false);
      setEditingPostId(null);
      setCurrentEditingPost(null);

      setTitle("");
      setContent("");
      setYoutubeVideoId("");

    } catch (error) {
      console.error("Save failed", error);
    } finally {
      setLoading(false);
      fetchAllBlogPosts()
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = allPosts.findIndex((post) => post.id === active.id);
    const newIndex = allPosts.findIndex((post) => post.id === over.id);
    const reorderedPosts = arrayMove(allPosts, oldIndex, newIndex);
    const updatedPosts = reorderedPosts.map((post, index) => ({
      ...post,
      orderNumber: index + 1,
    }));

    setAllPosts(updatedPosts);

    try {
      await axiosInstance.patch(
        `${process.env.REACT_APP_BASE_API}/posts/reorder`,
        updatedPosts.map(p => ({ id: p.id, orderNumber: p.orderNumber }))
      );
      console.log("New order:", updatedPosts.map(p => ({ id: p.id, orderNumber: p.orderNumber })));
    } catch (error) {
      console.error("Failed to update order:", error);
      fetchAllBlogPosts();
    }
  };

  return (
    <Box className=" posts-container">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box component="p" sx={{ fontSize: 18 }}>
          Blog Section, Sportisti App
        </Box>

        <Button
          sx={{ width: 200, color: "white" }}
          variant="contained"
          onClick={handleChange}
        >
          Krijo nje Postim
        </Button>
      </Box>

      {allPosts.length === 0 && (
        <Box
          sx={{
            color: "primary.main",
            fontSize: "1.5rem",
            mb: 1,
          }}
        >
          Nuk Keni artikuj te postuar !
        </Box>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={allPosts.map(p => p.id)} strategy={rectSortingStrategy}>
          <Grid container spacing={2} sx={{ padding: 2 }}>
            {Array.isArray(allPosts) &&
              allPosts.map((post) => (
                <SortableCard
                  key={post.id}
                  post={post}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                />
              ))}
          </Grid>
        </SortableContext>
      </DndContext>

   
      {showModal && (
        <Dialog fullWidth open>
          <DialogTitle color="primary">
            {editingPostId ? "Ndrysho Postimin" : "Krijo Postimin"}!
          </DialogTitle>

          {loading && (
            <Stack
              sx={{ color: "grey.500", my: 2 }}
              spacing={2}
              justifyContent="center"
              direction="row"
            >
              <CircularProgress color="success" />
            </Stack>
          )}

          <DialogContent sx={{ minHeight: 600, paddingBottom: 4 }}>
            <Grid container spacing={2} sx={{ pt: 3 }}>

              <Grid item xs={12}>
                <TextField
                  label="Titulli"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  type="text"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box 
                    component="label" 
                    sx={{ 
                      display: "block", 
                      mb: 1, 
                      fontSize: "0.875rem",
                      color: "text.secondary"
                    }}
                  >
                    Përmbajtja
                  </Box>
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                    placeholder="Shkruani përmbajtjen e postimit..."
                    style={{ 
                      height: "300px", 
                      marginBottom: "50px",
                      backgroundColor: "white"
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="YouTube Video ID (opsionale)"
                  value={youtubeVideoId}
                  onChange={(e) => setYoutubeVideoId(e.target.value)}
                  fullWidth
                  type="text"
                  placeholder="p.sh: dQw4w9WgXcQ"
                  helperText="Vendosni vetëm ID-në e videos YouTube (jo URL-në e plotë)"
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setShowModal(false)}
              variant="contained"
              color="error"
              disabled={loading}
            >
              Mbyll
            </Button>
            <Button
              onClick={handleSavePost}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {editingPostId ? "Përditëso" : "Ruaj"}
            </Button>
          </DialogActions>

        </Dialog>
      )}
    </Box>
  );
}
